/* === BEGIN imported: utils/storage.js (verbatim from plugin) === */
/**
 * Storage Utility
 * Type-safe wrapper for sessionStorage with JSON parsing and error handling
 */

import {
  STORAGE_KEYS,
  COUNTER_TYPES,
  COUNTER_KEY_PREFIXES,
  getCounterKey,
} from '@/constants/storageKeys';

/**
 * Storage utility object
 */
export const storage = {
  /**
   * Get a value from sessionStorage with automatic JSON parsing
   * @param {string} key - Storage key
   * @param {*} defaultValue - Default value if key doesn't exist
   * @returns {*} - The stored value or defaultValue
   */
  get(key, defaultValue = null) {
    try {
      const value = sessionStorage.getItem(key);

      if (value === null || value === undefined) {
        return defaultValue;
      }

      // Handle boolean strings (legacy compatibility)
      if (value === 'true') return true;
      if (value === 'false') return false;

      // Try to parse as JSON
      try {
        return JSON.parse(value);
      } catch {
        // Return as string if not valid JSON
        return value;
      }
    } catch (error) {
      console.warn(`Storage.get error for key "${key}":`, error);
      return defaultValue;
    }
  },

  /**
   * Set a value in sessionStorage with automatic JSON serialization
   * @param {string} key - Storage key
   * @param {*} value - Value to store
   */
  set(key, value) {
    try {
      let serialized;

      if (value === null || value === undefined) {
        sessionStorage.removeItem(key);
        return;
      }

      if (typeof value === 'object') {
        serialized = JSON.stringify(value);
      } else if (typeof value === 'boolean') {
        serialized = value.toString();
      } else {
        serialized = String(value);
      }

      sessionStorage.setItem(key, serialized);
    } catch (error) {
      console.warn(`Storage.set error for key "${key}":`, error);
    }
  },

  /**
   * Remove a key from sessionStorage
   * @param {string} key - Storage key
   */
  remove(key) {
    try {
      sessionStorage.removeItem(key);
    } catch (error) {
      console.warn(`Storage.remove error for key "${key}":`, error);
    }
  },

  /**
   * Check if a key exists in sessionStorage
   * @param {string} key - Storage key
   * @returns {boolean}
   */
  has(key) {
    return sessionStorage.getItem(key) !== null;
  },

  // ===== DOMAIN-SPECIFIC HELPERS =====

  /**
   * Get destinations array
   * @returns {Array}
   */
  getDestinations() {
    return this.get(STORAGE_KEYS.DESTINATIONS, []);
  },

  /**
   * Set destinations array
   * @param {Array} destinations
   */
  setDestinations(destinations) {
    this.set(STORAGE_KEYS.DESTINATIONS, destinations);
  },

  /**
   * Get counter value
   * @param {string} type - Counter type from COUNTER_TYPES
   * @param {string|number} id - Item ID
   * @returns {number}
   */
  getCounter(type, id) {
    const key = getCounterKey(type, id);
    const value = this.get(key, 0);
    return parseInt(value, 10) || 0;
  },

  /**
   * Set counter value
   * @param {string} type - Counter type from COUNTER_TYPES
   * @param {string|number} id - Item ID
   * @param {number} value - Counter value
   */
  setCounter(type, id, value) {
    const key = getCounterKey(type, id);
    this.set(key, value);
  },

  /**
   * Clear all counter keys
   */
  clearAllCounters() {
    // Get all sessionStorage keys
    const keys = Object.keys(sessionStorage);

    // Remove keys that match counter prefixes
    keys.forEach((key) => {
      if (COUNTER_KEY_PREFIXES.some((prefix) => key.startsWith(prefix))) {
        this.remove(key);
      }
    });
  },

};

// Export storage keys for convenience
export { STORAGE_KEYS, COUNTER_TYPES, getCounterKey };

export default storage;
/* === END imported: utils/storage.js === */
